import * as React from 'react';
import { ReactNode } from 'react';
import '../css/action-card.sass';
import Card from './Card';
import LinkButton from './LinkButton';
import RAPPopup from './RAPPopup';

interface ActionCardProps {
  children: ReactNode;
  style?: string;
  title: string;
  subTitle?: string;
  landingStatus: 'DISPLAY' | 'EDITABLE';
  status: 'DISPLAY' | 'EDITABLE' | 'UNSAVED' | 'SAVED';
  onSave?: Function;
  onCancel?: Function;
  onEdit?: Function;
  unSaveMsg?: string;
  inValid?: boolean;
  saveMsg?: string;
  editBtnLabel?: string;
  cancelBtnLabel?: string;
  saveBtnLabel?: string;
  summaryContent?: ReactNode;
  messages?: ReactNode;
  primaryLinkLabel?: string;
  secondaryLinkLabel?: string;
  onPrimaryLinkClick?: Function;
  onSecondaryLinkClick?: Function;
  titleNode?: ReactNode;
}

interface ActionCardState {
  displayRAPPopup: boolean;
}

export default function ActionCard(props: ActionCardProps) {
  const [state, setState] = React.useState<ActionCardState>({
    displayRAPPopup: false
  });

  const {
    saveMsg = 'Saved!',
    unSaveMsg = 'Unsaved Changes',
    editBtnLabel = 'Edit',
    cancelBtnLabel = 'Cancel',
    saveBtnLabel = 'Save'
  } = props;

  let displayCancelBtn;
  let displaySaveBtn;
  let displayEditBtn;
  let type = 'warning';
  let message = unSaveMsg;

  React.useEffect(() => {
    if (props.status === 'SAVED') {
      setState({ ...state, displayRAPPopup: false });
    }
    if (props.landingStatus === 'DISPLAY' && props.status === 'EDITABLE') {
      setState({ ...state, displayRAPPopup: true });
    }
  }, [props.status]);

  if (props.inValid) {
    displayCancelBtn = true;
    displaySaveBtn = true;
    type = 'warning';
    message = unSaveMsg;
  } else if (props.status === 'UNSAVED') {
    displayCancelBtn = true;
    displaySaveBtn = true;
    type = 'warning';
    message = unSaveMsg;
  } else if (props.status === 'SAVED') {
    displayCancelBtn = false;
    type = 'success';
    message = saveMsg;
    if (props.landingStatus === 'DISPLAY') {
      displayEditBtn = true;
    }
  } else if (props.status === 'DISPLAY') {
    displayEditBtn = true;
  }

  const editBtn = (
    <LinkButton
      name={`${props.title.replace(/[^\w]/g, '')}EditBtn`}
      id={`${props.title.replace(/[^\w]/g, '')}EditBtn`}
      smol
      onClick={() => {
        setState({ ...state, displayRAPPopup: true });
        if (props.onEdit !== undefined) {
          props.onEdit();
        }
      }}
    >
      {editBtnLabel}
    </LinkButton>
  );

  const actionCardContents = (
    <div className={`actionCard ${props.status.toLowerCase()}Status`}>
      <Card style={props.style}>
        <div
          className={`actionCardMessage cardMessage ${
            displayCancelBtn || props.status === 'SAVED' ? type : ''
          }${
            message && (displayCancelBtn || props.status === 'SAVED')
              ? ''
              : ' hidden'
          }`}
        >
          <span className="icon" />
          <div>{message}</div>
          <div
            style={{ width: '0px', height: '0px', opacity: 0 }}
            role="status"
            aria-live="polite"
          >
            {props.status !== 'EDITABLE' ? message : ' '}
          </div>
        </div>
        {props.messages && <div className="messages">{props.messages}</div>}
        {displayEditBtn && (
          <div
            className={`editLink${
              props.status === 'SAVED' ? '' : ' actionCardMessage'
            }`}
          >
            {editBtn}
          </div>
        )}
        <div className={`row ${props.summaryContent ? 'summary' : ''}`}>
          {props.titleNode ? (
            <div className="actionCardTitle">{props.titleNode}</div>
          ) : (
            <h3 className="actionCardTitle">{props.title}</h3>
          )}
        </div>
        {props.subTitle && <div className="subTitle">{props.subTitle}</div>}
        {props.summaryContent && (
          <div className="summaryContent">{props.summaryContent}</div>
        )}
        <div
          className={`actionCardEle ${props.summaryContent ? 'summary' : ''}${
            displayCancelBtn ? ' adjustPadding' : ''
          }`}
        >
          {props.children}
        </div>
        <div
          className={`btnContainer${
            displayCancelBtn ? ' adjustAlignment' : ''
          }`}
        >
          <div className={`alignBtn${displayCancelBtn ? '' : ' hidden'}`}>
            <LinkButton
              name={`${props.title.replace(/[^\w]/g, '')}CancelBtn`}
              id={`${props.title.replace(/[^\w]/g, '')}CancelBtn`}
              smol
              onClick={(e: any) => {
                setState({ ...state, displayRAPPopup: false });
                if (props.onCancel) {
                  props.onCancel(e);
                }
              }}
              type="secondary"
            >
              {cancelBtnLabel}
            </LinkButton>
          </div>
          <div
            className={`alignBtn alignRight${displaySaveBtn ? '' : ' hidden'}`}
          >
            <LinkButton
              name={`${props.title.replace(/[^\w]/g, '')}SaveBtn`}
              id={`${props.title.replace(/[^\w]/g, '')}SaveBtn`}
              smol
              disabled={props.inValid}
              onClick={(e: any) => {
                if (props.onSave) {
                  props.onSave(e);
                }
              }}
            >
              {saveBtnLabel}
            </LinkButton>
          </div>
        </div>
        {!displayEditBtn && props.secondaryLinkLabel && (
          <div
            className={`alignBtn alignSecondaryLinkBtn ${
              props.status !== 'UNSAVED' ? '' : ' hidden'
            }`}
          >
            <LinkButton
              name={`${props.title.replace(
                /[^\w]/g,
                ''
              )}${props.secondaryLinkLabel.replace(/[^\w]/g, '')}Btn`}
              id={`${props.title.replace(
                /[^\w]/g,
                ''
              )}${props.secondaryLinkLabel.replace(/[^\w]/g, '')}Btn`}
              smol
              onClick={(e: any) => {
                if (props.onSecondaryLinkClick !== undefined) {
                  props.onSecondaryLinkClick(e);
                }
              }}
              type="secondary"
            >
              {props.secondaryLinkLabel}
            </LinkButton>
          </div>
        )}
        {!displayEditBtn && props.primaryLinkLabel && (
          <div
            className={`alignBtn alignPrimaryLinkBtn${
              props.status !== 'UNSAVED' ? '' : ' hidden'
            }`}
          >
            <LinkButton
              name={`${props.title.replace(
                /[^\w]/g,
                ''
              )}${props.primaryLinkLabel.replace(/[^\w]/g, '')}Btn`}
              id={`${props.title.replace(
                /[^\w]/g,
                ''
              )}${props.primaryLinkLabel.replace(/[^\w]/g, '')}Btn`}
              smol
              onClick={(e: any) => {
                if (props.onPrimaryLinkClick !== undefined) {
                  props.onPrimaryLinkClick(e);
                }
              }}
            >
              {props.primaryLinkLabel}
            </LinkButton>
          </div>
        )}
      </Card>
    </div>
  );

  if (state.displayRAPPopup) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.removeProperty('overflow');
  }

  return (
    <>
      {state.displayRAPPopup ? (
        <RAPPopup
          toggleRAP={state.displayRAPPopup}
          onClose={(e: any) => {
            setState({ ...state, displayRAPPopup: false });
            if (props.onCancel) {
              props.onCancel(e);
            }
          }}
        >
          {actionCardContents}
        </RAPPopup>
      ) : (
        actionCardContents
      )}
    </>
  );
}
